'use client';

import React, { useEffect, useRef, useState } from 'react';

import Link from 'next/link';
import { useRouter } from 'next/navigation';

import { User } from '@supabase/supabase-js';

import { Menu } from 'lucide-react';

import { Button } from '@kit/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@kit/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@kit/ui/dropdown-menu';
import { NavigationMenu, NavigationMenuList } from '@kit/ui/navigation-menu';
import { Trans } from '@kit/ui/trans';

import pathsConfig from '~/config/paths.config';

import { ProductNav } from './product-nav';
import { useSession } from './site-header-account-section';
import { SiteNavigationItem } from './site-navigation-item';

interface LinkItem {
  label: string;
  path: string;
  requiresAuth?: boolean;
}

interface MobileDropdownProps {
  onNavItemClick: (
    e: React.MouseEvent<HTMLElement>,
    requiresAuth: boolean,
  ) => void;
  user: User | null;
}

const links: Record<string, LinkItem> = {
  Services: {
    label: 'marketing:services',
    path: '/services',
  },
  Pricing: {
    label: 'marketing:pricing',
    path: '/pricing',
  },
  Docs: {
    label: 'marketing:documentationMenu',
    path: '/docs',
  },
  FAQ: {
    label: 'marketing:faq',
    path: '/faq',
  },
  About: {
    label: 'marketing:about.weAreBimefy',
    path: '/about',
  },
  Contact: {
    label: 'marketing:contact',
    path: '/contact',
  },
};

export function SiteNavigation() {
  const session = useSession();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const router = useRouter();
  const triggerRef = useRef<HTMLSpanElement>(null);

  const handleNavItemClick = (
    e: React.MouseEvent<HTMLElement>,
    requiresAuth: boolean,
  ) => {
    if (requiresAuth && !session.data?.user) {
      e.preventDefault();
      setIsDialogOpen(true);
    }
  };

  const handleLogin = () => {
    setIsDialogOpen(false);
    router.push(`/auth/sign-in?next=${pathsConfig.app.personalAccountroadmap}`);
  };

  const handleGoBack = () => {
    setIsDialogOpen(false);
  };

  const NavItems = Object.values(links).map((item) => {
    const { path, label, requiresAuth } = item;

    if (requiresAuth) {
      return (
        <SiteNavigationItem key={path} path={path}>
          <span
            ref={triggerRef}
            onClick={(e) => handleNavItemClick(e, requiresAuth)}
          >
            <Trans i18nKey={label} />
          </span>
        </SiteNavigationItem>
      );
    }

    return (
      <SiteNavigationItem key={path} path={path}>
        <Trans i18nKey={label} />
      </SiteNavigationItem>
    );
  });

  const dialogContentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isDialogOpen) {
      dialogContentRef.current?.focus();
    }
  }, [isDialogOpen]);

  return (
    <>
      <div className={'hidden items-center justify-center md:flex'}>
        <NavigationMenu className={'px-4 py-2'}>
          <NavigationMenuList className={'gap-5'}>
            <ProductNav />
            {NavItems}
          </NavigationMenuList>
        </NavigationMenu>
      </div>

      <div className={'flex justify-start sm:items-center md:hidden'}>
        <MobileDropdown
          onNavItemClick={handleNavItemClick}
          user={session.data?.user ?? null}
        />
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent ref={dialogContentRef} tabIndex={-1}>
          <DialogHeader>
            <DialogTitle className="text-center">
              <Trans i18nKey={'marketing:authenticationRequired'} />
            </DialogTitle>
          </DialogHeader>

          <p className="mb-6 text-center">
            <Trans i18nKey="marketing:loginToRoadmap" />
          </p>
          <div className="flex justify-center gap-4">
            <Button onClick={handleGoBack} variant={'outline'}>
              <Trans i18nKey="goBack" />
            </Button>
            <Button onClick={handleLogin} variant={'default'}>
              <Trans i18nKey="Login" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function MobileDropdown({ onNavItemClick, user: _user }: MobileDropdownProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger aria-label={'Open Menu'}>
        <Menu className={'h-8 w-8'} />
      </DropdownMenuTrigger>

      <DropdownMenuContent className={'w-full'}>
        {Object.values(links).map((item) => {
          const { path, label, requiresAuth } = item;
          const className = 'flex w-full h-full items-center';

          if (requiresAuth) {
            return (
              <DropdownMenuItem key={path} asChild>
                <span
                  onClick={(e) => onNavItemClick(e, requiresAuth)}
                  className={`${className} cursor-pointer`}
                >
                  <Trans i18nKey={label} />
                </span>
              </DropdownMenuItem>
            );
          }

          return (
            <DropdownMenuItem key={path} asChild>
              <Link className={className} href={path}>
                <Trans i18nKey={label} />
              </Link>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
